/**
 * Open-Meteo customer API client (forecast + historical).
 * Ports behavior from iOS OpenMeteoWeatherManager.swift.
 */

import type {
  ForecastDay,
  HourlyForecast,
  OpenMeteoResponse,
  WeatherErrorCode,
} from '../types/weather';
import { weatherCodeToCondition, weatherCodeToIcon, isDaylightFromOpenMeteo } from '../utils/weatherIcons';
import { addDaysYmd } from '../utils/wallClock';

const FORECAST_BASE = 'https://customer-api.open-meteo.com/v1/forecast';
const HISTORICAL_BASE = 'https://customer-historical-forecast-api.open-meteo.com/v1/forecast';

export class WeatherServiceError extends Error {
  readonly code: WeatherErrorCode;
  readonly httpStatus?: number;

  constructor(code: WeatherErrorCode, message: string, httpStatus?: number) {
    super(message);
    this.name = 'WeatherServiceError';
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

function getApiKey(): string {
  return String(import.meta.env.VITE_OPEN_METEO_API_KEY || '').trim();
}

function appendApiKey(url: string): string {
  const apiKey = getApiKey();
  if (!apiKey) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}apikey=${encodeURIComponent(apiKey)}`;
}

function parseHourFromDateTime(dateTime: string): number {
  const timePart = dateTime.split('T')[1];
  if (!timePart) {
    return 0;
  }
  return Number.parseInt(timePart.slice(0, 2), 10);
}

function parseYmdParts(ymd: string): { year: number; month: number; day: number } {
  const [year, month, day] = ymd.split('-').map((part) => Number.parseInt(part, 10));
  return { year, month, day };
}

function formatYmd(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function localStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function enumerateDates(startYmd: string, endYmd: string): string[] {
  const dates: string[] = [];
  let current = startYmd;
  let guard = 0;
  while (current <= endYmd && guard < 20_000) {
    dates.push(current);
    current = addDaysYmd(current, 1);
    guard += 1;
  }
  return dates;
}

export function isValidWeatherCoords(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
    return false;
  }
  return !(Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001);
}

/** YYYY-MM-DD one calendar year earlier; Feb 29 becomes Feb 28 on non-leap years. */
export function previousYearDateString(ymd: string): string {
  const { year, month, day } = parseYmdParts(ymd);
  const previousYear = year - 1;
  const candidate = new Date(Date.UTC(previousYear, month - 1, day));
  if (candidate.getUTCMonth() + 1 !== month || candidate.getUTCDate() !== day) {
    return formatYmd(previousYear, month, 28);
  }
  return formatYmd(previousYear, month, day);
}

/** True when ymd is today or later in local calendar time. */
export function isYmdOnOrAfterLocalToday(ymd: string, today: Date = new Date()): boolean {
  const todayYmd = formatYmd(today.getFullYear(), today.getMonth() + 1, today.getDate());
  return ymd >= todayYmd;
}

async function fetchOpenMeteo(url: string): Promise<OpenMeteoResponse> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new WeatherServiceError('missingApiKey', 'Open-Meteo API key is not configured');
  }

  let response: Response;
  try {
    response = await fetch(appendApiKey(url));
  } catch (error) {
    throw new WeatherServiceError(
      'networkError',
      error instanceof Error ? error.message : 'Network request failed'
    );
  }

  if (response.status === 429) {
    throw new WeatherServiceError('rateLimitExceeded', 'Too many requests', response.status);
  }
  if (response.status >= 500) {
    throw new WeatherServiceError('serverError', 'Weather service temporarily unavailable', response.status);
  }
  if (!response.ok) {
    throw new WeatherServiceError('httpError', `HTTP ${response.status}`, response.status);
  }

  try {
    return (await response.json()) as OpenMeteoResponse;
  } catch {
    throw new WeatherServiceError('decodingError', 'Failed to process weather data');
  }
}

function mapDailyResponse(daily: NonNullable<OpenMeteoResponse['daily']>, isHistorical: boolean): ForecastDay[] {
  return daily.time.flatMap((date, index) => {
    const maxTemp = daily.temperature_2m_max[index];
    const minTemp = daily.temperature_2m_min[index];
    const precipRaw = daily.precipitation_probability_max[index];
    const weatherCode = daily.weathercode[index];

    if (maxTemp == null || minTemp == null || weatherCode == null) {
      return [];
    }

    return [
      {
        date,
        condition: weatherCodeToCondition(weatherCode),
        iconName: weatherCodeToIcon(weatherCode),
        highTempC: maxTemp,
        lowTempC: minTemp,
        precipitationProbability: precipRaw ?? 0,
        isHistorical,
        uvIndex: null,
      },
    ];
  });
}

function mapHourlyResponse(hourly: NonNullable<OpenMeteoResponse['hourly']>): HourlyForecast[] {
  return hourly.time.flatMap((dateTime, index) => {
    const tempC = hourly.temperature_2m[index];
    const precipRaw = hourly.precipitation_probability[index];
    const weatherCode = hourly.weathercode[index];
    const humidity = hourly.relative_humidity_2m[index];
    const windSpeed = hourly.wind_speed_10m[index];

    if (tempC == null || weatherCode == null || humidity == null || windSpeed == null) {
      return [];
    }

    const isDayRaw = hourly.is_day?.[index];
    const isDay = isDayRaw == null ? true : isDaylightFromOpenMeteo(isDayRaw);
    const date = dateTime.split('T')[0];
    return [
      {
        date,
        hour: parseHourFromDateTime(dateTime),
        condition: weatherCodeToCondition(weatherCode),
        iconName: weatherCodeToIcon(weatherCode, isDay),
        tempC,
        precipProbability: precipRaw ?? 0,
        humidity,
        windSpeed,
        isHistorical: false,
        isDay: isDayRaw == null ? null : isDay,
      },
    ];
  });
}

export async function fetchDailyForecast(lat: number, lng: number): Promise<ForecastDay[]> {
  if (!isValidWeatherCoords(lat, lng)) {
    throw new WeatherServiceError('invalidCoords', 'Invalid coordinates');
  }

  const url =
    `${FORECAST_BASE}?latitude=${lat}&longitude=${lng}` +
    '&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode' +
    '&timezone=auto&forecast_days=16';

  const response = await fetchOpenMeteo(url);
  if (!response.daily || response.daily.time.length === 0) {
    throw new WeatherServiceError('noDailyData', 'Daily forecast data unavailable');
  }

  return mapDailyResponse(response.daily, false);
}

export async function fetchHourlyForecast(lat: number, lng: number): Promise<HourlyForecast[]> {
  if (!isValidWeatherCoords(lat, lng)) {
    throw new WeatherServiceError('invalidCoords', 'Invalid coordinates');
  }

  const url =
    `${FORECAST_BASE}?latitude=${lat}&longitude=${lng}` +
    '&hourly=temperature_2m,precipitation_probability,weathercode,relative_humidity_2m,wind_speed_10m,is_day' +
    '&timezone=auto&forecast_days=16';

  const response = await fetchOpenMeteo(url);
  if (!response.hourly || response.hourly.time.length === 0) {
    throw new WeatherServiceError('noData', 'No hourly weather data available');
  }

  return mapHourlyResponse(response.hourly);
}

function getSeasonalWeatherData(
  month: number,
  latitude: number
): {
  condition: string;
  iconName: string;
  highTempC: number;
  lowTempC: number;
  precipitationProbability: number;
} {
  const isNorthernHemisphere = latitude > 0;

  switch (month) {
    case 12:
    case 1:
    case 2:
      if (isNorthernHemisphere) {
        return {
          condition: 'Partly Cloudy',
          iconName: 'cloud.sun.fill',
          highTempC: 5,
          lowTempC: -2,
          precipitationProbability: 30,
        };
      }
      return {
        condition: 'Clear',
        iconName: 'sun.max.fill',
        highTempC: 25,
        lowTempC: 15,
        precipitationProbability: 10,
      };
    case 3:
    case 4:
    case 5:
      if (isNorthernHemisphere) {
        return {
          condition: 'Partly Cloudy',
          iconName: 'cloud.sun.fill',
          highTempC: 15,
          lowTempC: 8,
          precipitationProbability: 40,
        };
      }
      return {
        condition: 'Partly Cloudy',
        iconName: 'cloud.sun.fill',
        highTempC: 20,
        lowTempC: 12,
        precipitationProbability: 30,
      };
    case 6:
    case 7:
    case 8:
      if (isNorthernHemisphere) {
        return {
          condition: 'Clear',
          iconName: 'sun.max.fill',
          highTempC: 25,
          lowTempC: 18,
          precipitationProbability: 20,
        };
      }
      return {
        condition: 'Partly Cloudy',
        iconName: 'cloud.sun.fill',
        highTempC: 15,
        lowTempC: 8,
        precipitationProbability: 40,
      };
    case 9:
    case 10:
    case 11:
      if (isNorthernHemisphere) {
        return {
          condition: 'Partly Cloudy',
          iconName: 'cloud.sun.fill',
          highTempC: 18,
          lowTempC: 10,
          precipitationProbability: 30,
        };
      }
      return {
        condition: 'Partly Cloudy',
        iconName: 'cloud.sun.fill',
        highTempC: 22,
        lowTempC: 14,
        precipitationProbability: 30,
      };
    default:
      return {
        condition: 'Partly Cloudy',
        iconName: 'cloud.sun.fill',
        highTempC: 20,
        lowTempC: 12,
        precipitationProbability: 30,
      };
  }
}

export function generateSeasonalEstimate(lat: number, lng: number, dateYmd: string): ForecastDay {
  const { month } = parseYmdParts(dateYmd);
  const seasonal = getSeasonalWeatherData(month, lat);

  return {
    date: dateYmd,
    condition: seasonal.condition,
    iconName: seasonal.iconName,
    highTempC: seasonal.highTempC,
    lowTempC: seasonal.lowTempC,
    precipitationProbability: seasonal.precipitationProbability,
    isHistorical: true,
    uvIndex: null,
  };
}

export async function fetchHistoricalDay(
  lat: number,
  lng: number,
  dateYmd: string,
  today: Date = new Date()
): Promise<ForecastDay | null> {
  if (!isValidWeatherCoords(lat, lng)) {
    return null;
  }

  const historicalDate = previousYearDateString(dateYmd);
  // Historical API rejects future dates (400). Far-future trip days often map to a
  // previous-year date that is still ahead of "today" — use seasonal instead.
  if (isYmdOnOrAfterLocalToday(historicalDate, today)) {
    return generateSeasonalEstimate(lat, lng, dateYmd);
  }

  const url =
    `${HISTORICAL_BASE}?latitude=${lat}&longitude=${lng}` +
    '&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode' +
    `&start_date=${historicalDate}&end_date=${historicalDate}&timezone=auto`;

  try {
    const response = await fetchOpenMeteo(url);
    if (!response.daily || response.daily.time.length === 0) {
      return generateSeasonalEstimate(lat, lng, dateYmd);
    }

    const maxTemp = response.daily.temperature_2m_max[0];
    const minTemp = response.daily.temperature_2m_min[0];
    const precipRaw = response.daily.precipitation_probability_max[0];
    const weatherCode = response.daily.weathercode[0];

    if (maxTemp == null || minTemp == null || weatherCode == null) {
      return generateSeasonalEstimate(lat, lng, dateYmd);
    }

    return {
      date: dateYmd,
      condition: weatherCodeToCondition(weatherCode),
      iconName: weatherCodeToIcon(weatherCode),
      highTempC: maxTemp,
      lowTempC: minTemp,
      precipitationProbability: precipRaw ?? 0,
      isHistorical: true,
      uvIndex: null,
    };
  } catch {
    return generateSeasonalEstimate(lat, lng, dateYmd);
  }
}

export async function fetchWeatherForDateRange(
  lat: number,
  lng: number,
  startYmd: string,
  endYmd: string
): Promise<ForecastDay[]> {
  if (!isValidWeatherCoords(lat, lng)) {
    return [];
  }

  const today = localStartOfDay(new Date());
  const todayYmd = formatYmd(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const allDates = enumerateDates(startYmd, endYmd);
  const results: ForecastDay[] = [];
  const covered = new Set<string>();

  const futureDates = allDates.filter((ymd) => ymd >= todayYmd);
  if (futureDates.length > 0) {
    try {
      const forecast = await fetchDailyForecast(lat, lng);
      for (const day of forecast) {
        if (day.date >= startYmd && day.date <= endYmd) {
          results.push(day);
          covered.add(day.date);
        }
      }
    } catch {
      // continue with historical for uncovered dates
    }
  }

  const pastDates = allDates.filter((ymd) => ymd < todayYmd);
  for (const ymd of pastDates) {
    const day = await fetchHistoricalDay(lat, lng, ymd);
    if (day) {
      results.push(day);
      covered.add(ymd);
    }
  }

  for (const ymd of allDates) {
    if (covered.has(ymd)) {
      continue;
    }
    const day = await fetchHistoricalDay(lat, lng, ymd);
    if (day) {
      results.push(day);
      covered.add(ymd);
    }
  }

  return results.sort((a, b) => a.date.localeCompare(b.date));
}
